// Coaching program create/edit form, shared by Admin → Coaching Programs and
// Admin → BC Registrations so both offer exactly the same options. Loads its
// own lists (owners, coaches, courts, other programs) and saves through
// /program-types; onSaved gets { id, name, message }.

import React, { useEffect, useState } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';
const EMPTY = { name: '', ownerIds: [], weeklyBudget: '', signupUrl: '', startDate: '', endDate: '', feederProgramTypeIds: [], schedule: [] };

function formFromProgram(pt) {
  const ownerIds = Array.isArray(pt.ownerIds) && pt.ownerIds.length > 0
    ? pt.ownerIds.slice()
    : (pt.ownerId ? [pt.ownerId] : []);
  return {
    name: pt.name,
    ownerIds,
    weeklyBudget: pt.weeklyBudget || '',
    signupUrl: pt.signupUrl || '',
    startDate: pt.startDate ? String(pt.startDate).split('T')[0] : '',
    endDate: pt.endDate ? String(pt.endDate).split('T')[0] : '',
    feederProgramTypeIds: Array.isArray(pt.feederProgramTypeIds) ? pt.feederProgramTypeIds.slice() : [],
    schedule: Array.isArray(pt.schedule)
      ? pt.schedule.map(s => ({
          dayOfWeek: s.dayOfWeek,
          startTime: s.startTime,
          durationMinutes: s.durationMinutes,
          court: s.court || '',
          coaches: Array.isArray(s.coaches)
            ? s.coaches.map(c => ({ coachId: c.coachId, coachQualificationId: c.coachQualificationId }))
            : []
        }))
      : []
  };
}

export default function CoachingProgramForm({ programId = null, initialName = '', onSaved, onCancel, subtitle }) {
  const [form, setForm] = useState({ ...EMPTY, name: initialName });
  const [programTypes, setProgramTypes] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [coachingCourts, setCoachingCourts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ptRes, peopleRes, coachRes, courtsRes] = await Promise.all([
          fetch(`${API_BASE}/program-types?includeArchived=true`),
          fetch(`${API_BASE}/person`),
          fetch(`${API_BASE}/coaches`),
          fetch(`${API_BASE}/coaching-courts`)
        ]);
        const pts = ptRes.ok ? await ptRes.json() : [];
        if (cancelled) return;
        setProgramTypes(Array.isArray(pts) ? pts : []);
        if (peopleRes.ok) setAllPeople(await peopleRes.json());
        if (coachRes.ok) setCoaches(await coachRes.json());
        if (courtsRes.ok) setCoachingCourts(await courtsRes.json());
        if (programId) {
          const pt = (Array.isArray(pts) ? pts : []).find(p => p.id === programId);
          if (pt) setForm(formFromProgram(pt));
          else setError('Coaching program not found.');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load the form.');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [programId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const url = programId ? `${API_BASE}/program-types/${programId}` : `${API_BASE}/program-types`;
      const response = await auditFetch(url, {
        method: programId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          ownerIds: Array.isArray(form.ownerIds) ? form.ownerIds.map(n => parseInt(n)).filter(Boolean) : [],
          weeklyBudget: form.weeklyBudget ? parseFloat(form.weeklyBudget) : 0,
          signupUrl: form.signupUrl || '',
          startDate: form.startDate || null,
          endDate: form.endDate || null,
          feederProgramTypeIds: Array.isArray(form.feederProgramTypeIds) ? form.feederProgramTypeIds : [],
          schedule: Array.isArray(form.schedule)
            ? form.schedule.map(s => ({
                dayOfWeek: parseInt(s.dayOfWeek, 10),
                startTime: s.startTime,
                durationMinutes: parseInt(s.durationMinutes, 10),
                court: s.court || null,
                coaches: Array.isArray(s.coaches)
                  ? s.coaches
                      .map(c => ({
                        coachId: parseInt(c.coachId, 10),
                        coachQualificationId: c.coachQualificationId == null ? null : parseInt(c.coachQualificationId, 10)
                      }))
                      .filter(c => Number.isFinite(c.coachId))
                      .slice(0, 5)
                  : []
              })).filter(s => Number.isFinite(s.dayOfWeek) && s.startTime && Number.isFinite(s.durationMinutes))
            : []
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Failed to save coaching program (HTTP ${response.status})`);
      const bits = [programId ? 'Coaching program updated.' : 'Coaching program added.'];
      if (data?.sessionsMaterialized) bits.push(`${data.sessionsMaterialized} new session(s) created.`);
      const ss = data?.syncStats;
      if (ss && (ss.kept || ss.deleted || ss.retained || ss.coachesAdded)) {
        const parts = [];
        if (ss.kept) parts.push(`${ss.kept} existing session(s) updated`);
        if (ss.coachesAdded) parts.push(`${ss.coachesAdded} default coach assignment(s) added`);
        if (ss.deleted) parts.push(`${ss.deleted} unattended session(s) removed (no longer in schedule)`);
        if (ss.retained) parts.push(`${ss.retained} already-attended session(s) preserved`);
        if (parts.length) bits.push('Future sessions sync: ' + parts.join('; ') + '.');
      }
      setSaving(false);
      onSaved({ id: programId || data.id, name: form.name, message: bits.join(' ') });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onCancel}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: subtitle ? '0.25rem' : '1rem' }}>
          <h3 style={{ margin: 0 }}>{programId ? 'Edit Coaching Program' : 'Add New Coaching Program'}</h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>
        {subtitle && <p style={{ margin: '0 0 1rem', color: '#666', fontSize: '0.85rem' }}>{subtitle}</p>}
        {error && <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '0.75rem' }}>{error}</div>}
        {!loaded ? <p style={{ color: '#888' }}>Loading…</p> : (
                <form onSubmit={handleSave} className="admin-form">
                  <div className="form-group">
                    <label>Name:</label>
                    <input type="text" placeholder="e.g., Junior Development" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Owners:</label>
                    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: '0.5rem', background: '#fafafa' }}>
                      {allPeople.map(p => {
                        const checked = (form.ownerIds || []).includes(p.id);
                        return (
                          <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.2rem 0', fontSize: '0.85rem' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const current = form.ownerIds || [];
                                setForm({
                                  ...form,
                                  ownerIds: checked ? current.filter(id => id !== p.id) : [...current, p.id]
                                });
                              }}
                            />
                            <span>{p.name}</span>
                          </label>
                        );
                      })}
                      {allPeople.length === 0 && <div style={{ fontSize: '0.8rem', color: '#999' }}>No people available.</div>}
                    </div>
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>
                      Tick one or more owners. When a coach declines a session, every owner with a mobile
                      number on file gets an SMS notification.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Weekly Budget ($):</label>
                    <input type="number" step="0.01" min="0" placeholder="e.g., 500.00" value={form.weeklyBudget} onChange={(e) => setForm({ ...form, weeklyBudget: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Signup URL:</label>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={form.signupUrl}
                      onChange={(e) => setForm({ ...form, signupUrl: e.target.value })}
                    />
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>
                      Sent via SMS to the contact mobile when someone allocates a participant from My Portal.
                    </small>
                  </div>
                  <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label>Start Date:</label>
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label>End Date:</label>
                      <input
                        type="date"
                        value={form.endDate}
                        onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                      />
                    </div>
                  </div>
                  <small style={{ color: '#666', fontSize: '0.75rem', display: 'block', marginTop: '-0.5rem' }}>
                    The program is auto-archived the day after the end date. Once archived it disappears from
                    the participant signup options.
                  </small>

                  {/* WEEKLY SCHEDULE BUILDER + LINKED/FEEDER ----------------
                      Hidden in EDIT mode so the form fits on screen — those
                      pieces are usually set up once at create time and the
                      Add form is the right place for them. Use the Coach
                      Planner's session tile to tweak per-session schedules
                      after the fact, or Admin → BC Registrations to fan out new ones.
                  */}
                  {!programId && (<>
                  <div className="form-group">
                    <label>Weekly Schedule:</label>
                    <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: '0.5rem', background: '#fafafa' }}>
                      {(form.schedule || []).length === 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#888', padding: '0.4rem 0.2rem' }}>
                          No sessions yet — add a row below to schedule weekly recurring sessions.
                        </div>
                      )}
                      {(form.schedule || []).map((entry, idx) => {
                        const updateRow = (patch) => {
                          const next = (form.schedule || []).slice();
                          next[idx] = { ...next[idx], ...patch };
                          setForm({ ...form, schedule: next });
                        };
                        const coachesForRow = Array.isArray(entry.coaches) ? entry.coaches : [];
                        const updateCoach = (cidx, patch) => {
                          const nextCoaches = coachesForRow.slice();
                          nextCoaches[cidx] = { ...nextCoaches[cidx], ...patch };
                          updateRow({ coaches: nextCoaches });
                        };
                        const removeCoach = (cidx) => {
                          updateRow({ coaches: coachesForRow.filter((_, i) => i !== cidx) });
                        };
                        const addCoach = () => {
                          if (coachesForRow.length >= 5) return;
                          updateRow({ coaches: [...coachesForRow, { coachId: '', coachQualificationId: null }] });
                        };
                        return (
                        <div key={idx} style={{ padding: '0.5rem 0.4rem', borderBottom: '1px solid #eee' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.9fr 0.7fr 1.1fr auto', gap: '0.4rem', alignItems: 'center' }}>
                            <select
                              value={entry.dayOfWeek}
                              onChange={(e) => updateRow({ dayOfWeek: parseInt(e.target.value, 10) })}
                              style={{ padding: '0.3rem', fontSize: '0.85rem' }}
                            >
                              <option value={1}>Monday</option>
                              <option value={2}>Tuesday</option>
                              <option value={3}>Wednesday</option>
                              <option value={4}>Thursday</option>
                              <option value={5}>Friday</option>
                              <option value={6}>Saturday</option>
                              <option value={0}>Sunday</option>
                            </select>
                            <input
                              type="time"
                              value={entry.startTime || ''}
                              onChange={(e) => updateRow({ startTime: e.target.value })}
                              style={{ padding: '0.3rem', fontSize: '0.85rem' }}
                            />
                            <input
                              type="number"
                              min="1"
                              placeholder="min"
                              value={entry.durationMinutes ?? ''}
                              onChange={(e) => updateRow({ durationMinutes: e.target.value })}
                              style={{ padding: '0.3rem', fontSize: '0.85rem' }}
                            />
                            <select
                              value={entry.court || ''}
                              onChange={(e) => updateRow({ court: e.target.value })}
                              style={{ padding: '0.3rem', fontSize: '0.85rem' }}
                            >
                              <option value="">No court</option>
                              {coachingCourts.filter(c => !c.isArchived).map(c => (
                                <option key={c.id} value={c.name}>{c.name}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => {
                                const next = (form.schedule || []).filter((_, i) => i !== idx);
                                setForm({ ...form, schedule: next });
                              }}
                              title="Remove this session"
                              style={{ background: 'transparent', border: 'none', color: '#c62828', fontSize: '1.1rem', cursor: 'pointer', padding: '0 0.3rem' }}
                            >&times;</button>
                          </div>

                          {/* Default coaches for this session row (up to 5).
                              Each picker encodes (coachId, coachQualificationId)
                              so the same person can appear with different
                              qualification levels. */}
                          <div style={{ marginTop: '0.4rem', paddingLeft: '0.2rem' }}>
                            <div style={{ fontSize: '0.72rem', color: '#666', marginBottom: '0.25rem' }}>
                              Default coaches ({coachesForRow.length}/5)
                            </div>
                            {coachesForRow.map((c, cidx) => {
                              const value = `${c.coachId || ''}_${c.coachQualificationId || ''}`;
                              return (
                                <div key={cidx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.3rem', marginBottom: '0.2rem' }}>
                                  <select
                                    value={value}
                                    onChange={(e) => {
                                      const [cidStr, qidStr] = e.target.value.split('_');
                                      const cid = cidStr ? parseInt(cidStr, 10) : '';
                                      const qid = qidStr ? parseInt(qidStr, 10) : null;
                                      updateCoach(cidx, { coachId: cid, coachQualificationId: qid });
                                    }}
                                    style={{ padding: '0.25rem', fontSize: '0.78rem' }}
                                  >
                                    <option value="_">— pick a coach —</option>
                                    {coaches.map(co => (
                                      <option
                                        key={`${co.id}_${co.coachQualificationId || 'x'}`}
                                        value={`${co.id}_${co.coachQualificationId || ''}`}
                                      >
                                        {co.name}{co.qualificationLevel ? ` — ${co.qualificationLevel}` : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => removeCoach(cidx)}
                                    title="Remove this coach"
                                    style={{ background: 'transparent', border: 'none', color: '#c62828', fontSize: '0.95rem', cursor: 'pointer', padding: '0 0.4rem' }}
                                  >&times;</button>
                                </div>
                              );
                            })}
                            {coachesForRow.length < 5 && (
                              <button
                                type="button"
                                onClick={addCoach}
                                style={{ marginTop: '0.15rem', padding: '0.2rem 0.55rem', fontSize: '0.72rem', background: 'white', color: '#1565c0', border: '1px solid #1565c0', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                              >+ Add coach</button>
                            )}
                          </div>
                        </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          const next = (form.schedule || []).slice();
                          next.push({ dayOfWeek: 1, startTime: '06:00', durationMinutes: 60, court: '', coaches: [] });
                          setForm({ ...form, schedule: next });
                        }}
                        style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem', fontSize: '0.8rem', background: '#2e7d32', color: 'white', border: '1px solid #1b5e20', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                      >+ Add session row</button>
                    </div>
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>
                      Each row is a weekly recurring session. Tick up to 5 default coaches
                      per row — when sessions are created in the Coach Planner, those coaches
                      are auto-allocated with their selected qualification level. Existing
                      sessions at the same start time aren't duplicated.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Linked / Feeder Programs:</label>
                    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: '0.5rem', background: '#fafafa' }}>
                      {programTypes
                        .filter(other => !other.isArchived && other.id !== programId)
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map(other => {
                          const checked = (form.feederProgramTypeIds || []).includes(other.id);
                          return (
                            <label key={other.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.2rem 0', fontSize: '0.85rem' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const current = form.feederProgramTypeIds || [];
                                  setForm({
                                    ...form,
                                    feederProgramTypeIds: checked
                                      ? current.filter(id => id !== other.id)
                                      : [...current, other.id]
                                  });
                                }}
                              />
                              <span>{other.name}{other.code ? ` (${other.code})` : ''}</span>
                            </label>
                          );
                        })}
                      {programTypes.filter(o => !o.isArchived && o.id !== programId).length === 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#999' }}>No other Coaching Programs to link.</div>
                      )}
                    </div>
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>
                      Tick any other Coaching Program whose participants should also appear in this program's
                      sessions for check-in. Useful for combined groups (e.g. include "Term 2 Squad" participants
                      in a "Friday Combined Session"). Adding a feeder backfills attendance for upcoming sessions.
                    </small>
                  </div>
                  </>)}
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit" disabled={saving}>{saving ? 'Saving…' : programId ? 'Update Coaching Program' : 'Add Coaching Program'}</button>
                    <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
                  </div>
                </form>
        )}
      </div>
    </div>
  );
}
